import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@maxus/website/components'
import { cn } from '@maxus/website/utils'

import { PlatformOption } from './type'

export type ModificationMaster = {
  id: number
  platforms: string[]
}

export const ModificationMasterRow = ({
  disabledPlatformIds,
  index,
  master,
  onClearPlatforms,
  onRemove,
  onTogglePlatform,
  platformOptions,
  removable,
}: {
  disabledPlatformIds: Set<string>
  index: number
  master: ModificationMaster
  onClearPlatforms: (id: number) => void
  onRemove: (id: number) => void
  onTogglePlatform: (id: number, platform: string, checked: boolean) => void
  platformOptions: PlatformOption[]
  removable?: boolean
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const keepOpenAfterSelectionRef = useRef(false)
  const selectedLabels = platformOptions
    .filter(option => master.platforms.includes(option.id))
    .map(option => option.name)
  const selectedLabel = selectedLabels.length ? selectedLabels.join('、') : t('Please select')

  return (
    <div
      className={cn(
        'grid items-center gap-3',
        removable ? 'grid-cols-[48px_minmax(0,1fr)_24px]' : 'grid-cols-[48px_minmax(0,1fr)]',
      )}
      data-slot="mmam-modification-master-row"
    >
      <div className="whitespace-nowrap text-sm font-semibold text-text-primary">
        {t('Master')} {index + 1} *
      </div>
      <SelectPrimitive.Root
        onOpenChange={nextOpen => {
          if (!nextOpen && keepOpenAfterSelectionRef.current) {
            keepOpenAfterSelectionRef.current = false
            return
          }

          setOpen(nextOpen)
        }}
        onValueChange={platform => {
          keepOpenAfterSelectionRef.current = true
          onTogglePlatform(master.id, platform, !master.platforms.includes(platform))
        }}
        open={open}
        value={`__master-${master.id}-platforms__`}
      >
        <SelectPrimitive.Trigger asChild>
          <button
            aria-label={t('Master {{n}} platforms', { n: index + 1 })}
            className={cn(
              'group/master-select flex h-[46px] w-full min-w-0 items-center justify-between gap-3 rounded-lg border border-field-line bg-field px-3 text-left text-sm shadow-none transition-colors hover:border-field-line-hover hover:bg-field-hover focus-visible:border-brand-primary focus-visible:bg-field-focus focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring',
              selectedLabels.length ? 'text-text-primary' : 'text-field-placeholder',
            )}
            onPointerDown={event => {
              if (!selectedLabels.length) {
                return
              }

              const rect = event.currentTarget.getBoundingClientRect()
              const clearZoneWidth = 32

              if (event.clientX >= rect.right - clearZoneWidth) {
                event.preventDefault()
                event.stopPropagation()
                onClearPlatforms(master.id)
              }
            }}
            type="button"
          >
            <SelectPrimitive.Value asChild>
              <span className="min-w-0 truncate">{selectedLabel}</span>
            </SelectPrimitive.Value>
            <span className="relative grid size-4 shrink-0 place-items-center text-text-tertiary">
              <SelectPrimitive.Icon asChild>
                <ChevronDown
                  className={cn(
                    'size-4 transition-opacity',
                    selectedLabels.length && 'group-hover/master-select:opacity-0',
                  )}
                />
              </SelectPrimitive.Icon>
              {selectedLabels.length > 0 && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 grid place-items-center opacity-0 transition-opacity group-hover/master-select:opacity-100"
                  data-slot="multi-select-clear"
                  title={t('Clear master {{n}} platforms', { n: index + 1 })}
                >
                  <X className="size-4" />
                </span>
              )}
            </span>
          </button>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            align="start"
            className="z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-border-subtle bg-surface-card p-1 text-sm text-text-secondary shadow-popover"
            position="popper"
            sideOffset={0}
          >
            <SelectPrimitive.Viewport>
              {platformOptions.map(option => {
                const selected = master.platforms.includes(option.id)

                return (
                  <SelectPrimitive.Item
                    className={cn(
                      'relative flex h-9 cursor-default select-none items-center rounded-md px-3 pr-9 outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:text-text-disabled data-[highlighted]:bg-surface-active data-[highlighted]:text-text-primary',
                      selected && 'bg-surface-active text-text-primary',
                    )}
                    disabled={!selected && disabledPlatformIds.has(option.id)}
                    key={option.id}
                    value={option.id}
                  >
                    <SelectPrimitive.ItemText>{option.name}</SelectPrimitive.ItemText>
                    {selected && (
                      <span className="absolute right-3 flex size-4 items-center justify-center text-text-primary">
                        <Check className="size-4" />
                      </span>
                    )}
                  </SelectPrimitive.Item>
                )
              })}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
      {removable && (
        <Button
          aria-label={t('Remove master {{n}}', { n: index + 1 })}
          className="text-text-tertiary hover:text-text-primary"
          onClick={() => onRemove(master.id)}
          size="icon-xs"
          variant="ghost"
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  )
}
